'use client'

import { useEffect, useState } from 'react'
import { cn } from '@nwwp/ui-shadcn'
import { ScheduleCallCTA } from '~/components/ScheduleCallCTA'
import { ONTRACK_CALENDLY_HREF } from './unlock-state'

/** The bar steps aside while an in-page booking CTA is on screen, so it never covers one. */
const ONTRACK_INLINE_CTA_IDS = ['ontrack-results-cta', 'ontrack-callout-cta'] as const

function useInlineCtaVisible(): boolean {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const onScreen = new Set<Element>()
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => (entry.isIntersecting ? onScreen.add(entry.target) : onScreen.delete(entry.target)))
      setVisible(onScreen.size > 0)
    })
    ONTRACK_INLINE_CTA_IDS.forEach((id) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [])

  return visible
}

interface MobileFloatingBarProps {
  onClose: () => void
}

export function MobileFloatingBar({ onClose }: MobileFloatingBarProps) {
  const hidden = useInlineCtaVisible()

  return (
    <div
      inert={hidden}
      className={cn(
        'dark fixed inset-x-0 bottom-0 z-100 flex items-center gap-3 bg-green-darker pt-3 pr-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] pl-4 shadow-[0_-8px_24px_rgba(0,0,0,0.2)] transition-transform duration-300 motion-reduce:transition-none tablet:hidden',
        hidden && 'translate-y-full'
      )}
    >
      <p className="flex-1 text-body-sm-bold text-white">Talk to a fiduciary advisor</p>
      <ScheduleCallCTA
        segmentElement="OnTrack Mobile Sticky Bar"
        baseUrl={ONTRACK_CALENDLY_HREF}
        variant="funnel"
        size="cta-sm"
        className="shrink-0 px-5"
      >
        Book a call
      </ScheduleCallCTA>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="flex size-11 shrink-0 items-center justify-center rounded-full text-body text-white/60 hover:text-white focus-visible:outline-2 focus-visible:outline-green-lighter"
      >
        <span aria-hidden="true" className="text-body">
          ✕
        </span>
      </button>
    </div>
  )
}
